import { useEffect } from 'react';
import {
    Navigate, generatePath, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { getLocalStorage, clearLocalStorage } from 'helpers/localStorage/helpers';
import { PageSpecificKeys } from 'helpers/localStorage';
import { SignUpStage } from 'views/public/SignUp/SignUpContext';
import { useGetProForma } from 'queries/ProForma';
import { useGetUploadedProForma } from 'queries/UploadedProForma';
import useAuthContext from 'contexts/AuthContext';
import { AxiosError } from 'axios';
import useGetDealOrganization from 'views/Deals/hooks/useGetDealOrganization';
import { URLS } from './services/urls';

const AppRouterRedirectHandler = ({ children }: { children: JSX.Element }) => {
    const location = useLocation();
    const { dealId, proFormaId } = useParams();
    const navigate = useNavigate();
    const signUpStage = getLocalStorage(PageSpecificKeys.SignUpStage);
    const { selectedOrganizationId, handleUpdateSelectedOrganization, userOrganizations } = useAuthContext();

    const { data: dealOrganization } = useGetDealOrganization({
        dealId: Number(dealId),
    });

    useEffect(() => {
        if (dealOrganization && dealOrganization.correlationId !== selectedOrganizationId) {
            const hasAccessToOrg = userOrganizations.some(
                org => org.organization.id === dealOrganization.id,
            );

            if (hasAccessToOrg) {
                handleUpdateSelectedOrganization(dealOrganization.id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealOrganization?.correlationId, selectedOrganizationId, userOrganizations, handleUpdateSelectedOrganization]);

    const { data: proForma, error: proFormaError } = useGetProForma(Number(dealId), Number(proFormaId), {
        enabled: !!dealId && !!proFormaId && !window.location.pathname.includes('upload'),
    });

    const { data: uploadedProForma, error: uploadedProFormaError } = useGetUploadedProForma(
        selectedOrganizationId!, Number(proFormaId), {
            enabled: !!dealId && !!proFormaId && !!selectedOrganizationId && window.location.pathname.includes('upload'),
        },
    );

    useEffect(() => {
        if (proFormaError) {
            const error = proFormaError as AxiosError;
            if (error.response?.status === 403 || proForma?.dealId !== Number(dealId)) {
                navigate(generatePath(URLS.PRO_FORMA.HOME, { dealId: String(dealId) }), { replace: true });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proForma, proFormaError]);

    useEffect(() => {
        if (uploadedProFormaError) {
            const error = uploadedProFormaError as AxiosError;
            if (error.response?.status === 403 || uploadedProForma?.dealId !== Number(dealId)) {
                navigate(generatePath(URLS.PRO_FORMA.HOME, { dealId: String(dealId) }), { replace: true });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedProForma, uploadedProFormaError]);

    let redirectPath: string | undefined;
    if (signUpStage === SignUpStage.ACCOUNT_CREATED) {
        redirectPath = URLS.ACCOUNT_CREATED;
    } else if (signUpStage === SignUpStage.DIRECT_SIGN_UP_COMPLETED) {
        redirectPath = URLS.DEALS.HOME;
        clearLocalStorage(PageSpecificKeys.SignUpStage);
        clearLocalStorage(PageSpecificKeys.SignUpData);
        clearLocalStorage(PageSpecificKeys.IsDirectSignUpFlow);
    }

    if (redirectPath && location.pathname !== redirectPath) {
        return <Navigate to={redirectPath} replace />;
    }

    return children;
};

export default AppRouterRedirectHandler;
